import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captchaAPI } from '@/utils/captchaApi';

/** 与后端命中容差接近，用于判定是否点击了已有标记 */
const MARKER_HIT_TOLERANCE = 24;

function findNearClickIndex(clicks, point) {
  return clicks.findIndex((pt) => {
    const dx = pt.x - point.x;
    const dy = pt.y - point.y;
    return Math.sqrt(dx * dx + dy * dy) <= MARKER_HIT_TOLERANCE;
  });
}

export default function ClickCaptchaModal({
  open,
  scene,
  onClose,
  onVerified,
  onError,
  /** 为 true 时点击遮罩不关闭（需完成验证） */
  dismissOnOverlay = true,
}) {
  const { t } = useTranslation('login');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [clicks, setClicks] = useState([]);
  const imageRef = useRef(null);

  const loadChallenge = useCallback(async () => {
    setLoading(true);
    setClicks([]);
    setChallenge(null);
    try {
      const data = await captchaAPI.getChallenge(scene);
      setChallenge(data);
    } catch (err) {
      onError?.(err.message || t('auth.captchaLoadFailed'));
      if (dismissOnOverlay) {
        onClose?.();
      }
    } finally {
      setLoading(false);
    }
  }, [scene, onError, onClose, dismissOnOverlay, t]);

  useEffect(() => {
    if (open) {
      loadChallenge();
    } else {
      setChallenge(null);
      setClicks([]);
    }
  }, [open, loadChallenge]);

  const toImageCoords = (clientX, clientY) => {
    const img = imageRef.current;
    if (!img || !challenge) return null;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const scaleX = challenge.imageWidth / rect.width;
    const scaleY = challenge.imageHeight / rect.height;
    return {
      x: Math.round((clientX - rect.left) * scaleX),
      y: Math.round((clientY - rect.top) * scaleY),
    };
  };

  const handleImageClick = (e) => {
    if (loading || verifying || !challenge) return;
    const expected = challenge.promptChars?.length || 0;
    const point = toImageCoords(e.clientX, e.clientY);
    if (!point) return;

    setClicks((prev) => {
      const existingIndex = findNearClickIndex(prev, point);
      if (existingIndex >= 0) {
        return prev.filter((_, i) => i !== existingIndex);
      }
      if (prev.length >= expected) {
        return prev;
      }
      return [...prev, point];
    });
  };

  const handleConfirm = async () => {
    if (!challenge || verifying) return;
    const expected = challenge.promptChars?.length || 0;
    if (clicks.length !== expected) {
      onError?.(t('auth.captchaNeedClicks', { count: expected }));
      return;
    }
    setVerifying(true);
    try {
      const result = await captchaAPI.verify({
        captchaId: challenge.captchaId,
        scene,
        clicks,
      });
      onVerified?.(result.captchaToken);
      onClose?.();
    } catch (err) {
      onError?.(err.message || t('auth.captchaVerifyFailed'));
      await loadChallenge();
    } finally {
      setVerifying(false);
    }
  };

  const handleOverlayClick = () => {
    if (dismissOnOverlay) {
      onClose?.();
    }
  };

  if (!open) return null;

  const expectedCount = challenge?.promptChars?.length || 0;

  return (
    <div className="click-captcha-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="click-captcha-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('auth.captchaAria')}
      >
        <div className="click-captcha-modal__header">
          <span>{t('auth.captchaPrompt')}</span>
          <span className="click-captcha-modal__prompt">
            {(challenge?.promptChars || []).map((ch, idx) => (
              <em key={`${ch}-${idx}`}>{ch}</em>
            ))}
          </span>
        </div>

        <div className="click-captcha-modal__body">
          {loading ? (
            <div className="click-captcha-modal__loading">{t('auth.captchaLoading')}</div>
          ) : (
            <div className="click-captcha-modal__image-box">
              <img
                ref={imageRef}
                className="click-captcha-modal__image"
                src={challenge?.backgroundImage}
                alt={t('auth.captchaAlt')}
                draggable={false}
                onClick={handleImageClick}
              />
              {clicks.map((pt, idx) => (
                <span
                  key={`${pt.x}-${pt.y}-${idx}`}
                  className="click-captcha-modal__marker"
                  style={{
                    left: `${(pt.x / challenge.imageWidth) * 100}%`,
                    top: `${(pt.y / challenge.imageHeight) * 100}%`,
                  }}
                  aria-hidden="true"
                >
                  {idx + 1}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="click-captcha-modal__footer">
          <div className="click-captcha-modal__tools">
            {dismissOnOverlay ? (
              <button type="button" className="click-captcha-modal__icon-btn" onClick={onClose} title={t('auth.close')}>
                ✕
              </button>
            ) : null}
            <button
              type="button"
              className="click-captcha-modal__icon-btn"
              onClick={loadChallenge}
              disabled={loading || verifying}
              title={t('auth.refresh')}
            >
              ↻
            </button>
          </div>
          <button
            type="button"
            className="click-captcha-modal__confirm"
            onClick={handleConfirm}
            disabled={loading || verifying || clicks.length !== expectedCount}
          >
            {verifying ? t('auth.verifying') : t('auth.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export const IPE_USER_SMS_CAPTCHA_SCENE = 'ipe-user-sms';
